// src/encounter/encounter-section-chrome.tsx
// PROD-1361 Slice 3 (Phase B) — the section chrome above the §6a block-grid, per the
// Encounter-2 frame 1736:151233: a section header (identity tile + title + action icons), a
// group chip-rail (jump-to-card + interpolated "Clear <section>"), and an ADDED NOTES block.
//
// Anti-fiction, and note this file changed which way it reads that principle (Andrew,
// 2026-08-20). It used to ship backend-less affordances VISIBLE but DISABLED to match the frame.
// The header action row no longer does: a control a clinician cannot use is itself the fiction,
// and two permanently dead icons taught people to ignore the row that also holds the live ones.
// The row now renders an action only where a handler exists —
//   • mic (dictation) → PROD-1366 and section edit are GONE until they have one
//   • ✦ → "Add Order" where the host wires it, absent on wrap-up where it deliberately doesn't
//   • comment → the section note toggle, live when `onToggleNotes` is supplied
//   • ADDED NOTES editor → still ENABLED (PROD-1368) when a `notes` binding is supplied (the
//     section authored a free-text narrative item, PROD-1365); DISABLED otherwise (no fiction).
// The disabled-not-hidden reading still governs the NOTES block; only the icon row changed.
// Presentational only: no data fetch; all behaviour flows in via props.
//
// C4 / PROD-1588 (Samer 2026-07-20): the DS-graduated `EncounterSectionChrome` organism is
// the canonical SECTION-level header/frame. Since DS 1.52 every questionnaire section —
// hpi · risk · ros · pe (plus docs/assessment-plan/wrap-up elsewhere) — has an
// `EncounterSectionKind` glyph, so `kind` is REQUIRED and the wave-4 kind-less legacy
// layout is retired (cure adoptions, 2026-07-22).
//
// ADDED NOTES (cure adoptions): the saved-note card is the DS `EncounterNoteEditor`
// molecule (1.52, E1 nodes 1098:420652/1098:420689) composed into the DS chrome's `notes`
// slot — the app-local AddedNotesEditor (Flutter CommentWidget port) retired in the same
// motion. Read/save handlers ride the same QuestionnaireResponse `onChange` seam 1:1
// (Save → onChange(trimmed) · Delete → onChange('')). Known deltas vs the retired app
// editor, accepted as DS anatomy: no Show more/less clamp, no empty-starts-in-edit, and
// `maxLength` is not enforced by the DS card (authored maxLength still validates on save
// server-side; DS seam flagged upstream).
import {
  EncounterNoteEditor,
  EncounterSectionChrome as DsEncounterSectionChrome,
  EncounterSectionRailGroups,
  SectionAddedNotes,
  type EncounterSectionChromeRailBorder,
  type EncounterSectionChromeTitleTracking,
  type EncounterSectionKind,
  type EncounterSectionWayfind,
} from '@scripsteam/scrips-signal-ds';
import { MessageSquare, Plus, type LucideIcon } from 'lucide-react';
import { SectionIconAction } from './section-icon-action';
import { useDesignV2 } from './lib/design-v2';

/** Design sheet v1.1 (switch ON only): the SOAP category for the solid badge, from the SAME grouping
 *  the S·O·A·P ribbon uses (lib/soap-ribbon.ts `sectionOf`): HPI · Risk · ROS = Subjective,
 *  PE · Docs = Objective, A&P = Assessment, Wrap-up = other. Passed explicitly because the DS
 *  derivation reads Risk and Docs as `other`, which would split one ribbon section in two colours. */
// Named `CHROME_SECTION_WAYFIND` (like the chart's CHART_SECTION_WAYFIND), not `SECTION_WAYFIND`: signal-ds
// 2.19 exports a `SECTION_WAYFIND` of its own — a different map (S/O/A/P letter → note wayfind category) —
// and the DS shadow guard reads the same name as a duplicate.
const CHROME_SECTION_WAYFIND: Record<EncounterSectionKind, EncounterSectionWayfind> = {
  hpi: 'subjective',
  risk: 'subjective',
  ros: 'subjective',
  pe: 'objective',
  docs: 'objective',
  'assessment-plan': 'assessment',
  'wrap-up': 'other',
};

export interface EncounterSectionChromeGroup {
  linkId: string;
  title: string;
  /** Per-group completion (wave-2 R3/B6 seam): true = filled success dot · false = hollow ·
   *  omit = no dot. The host derives it from the QuestionnaireResponse (groupHasAnswer). */
  complete?: boolean;
}

/**
 * ADDED NOTES binding (PROD-1368). The section's free-text narrative item (a `-narrative` linkId,
 * e.g. `hpi-cc-narrative`) surfaced as the ADDED NOTES editor. Supplied ONLY when the section
 * authored such an item; when omitted the block renders disabled (anti-fiction). The host owns
 * persistence — `onChange` writes back into the single QuestionnaireResponse (PROD-1234) and the
 * encounter's existing Save persists it; edit/expand UI state lives inside the DS molecule.
 */
export interface EncounterSectionChromeNotes {
  /** Stable identity of the narrative item — keys the editor so switching sections resets it. */
  linkId: string;
  /** The narrative item's label (item.text) — names the editor for assistive tech. */
  label?: string;
  /** Current narrative value (read from the QuestionnaireResponse). */
  value: string;
  /** Persist a new value; an empty string clears the narrative. */
  onChange: (value: string) => void;
  /** Authored max length, if any (the field's `maxLength`). NOTE: the DS EncounterNoteEditor
   *  exposes no maxLength seam yet (flagged upstream) — kept on the binding for the day it does. */
  maxLength?: number;
}

export interface EncounterSectionChromeProps {
  /** The section's top-level groups → the chip-rail entries. */
  groups: EncounterSectionChromeGroup[];
  /** Clear every answer in this section (the chip-rail's interpolated "Clear <section>"). */
  onClearSection: () => void;
  /**
   * SECTION-level mark-all action (PROD-1588 interaction fix I2 — Flutter
   * `makeAllItemNegative`): one tap sets every tri-state symptom-chip in the section to its
   * negative/normal state. Rides the DS chrome's `onAllNeg` rail end-slot seam. Supply ONLY when
   * the section actually renders chips AND the encounter isn't signed/locked (anti-fiction + a
   * finalized note must not be bulk-mutated); omitted → no link renders.
   */
  onAllNeg?: () => void;
  /**
   * Copy for the action above. Left unset the DS applies its per-kind default (ROS "All Neg" ·
   * PE "All Normal").
   *
   * Exists because the SECTION-level control and the PER-GROUP one inside QuestionnaireRenderer
   * are two different surfaces with two independent labels, so PE could read "All Normal" here
   * and "All Neg" three inches below. The design specifies one wording for both (2026-08-06,
   * owner's instruction), and this is the only seam that can align them.
   */
  allNegLabel?: string;
  /**
   * Section title override. The DS defaults to its canonical full name for the `kind`
   * ("History Of Present Illness"); the EncounterScreen design titles these screens
   * differently — HPI reads "Chief Complaint" (line 338) and ROS reads "Review of Systems"
   * with a lower-case "of" (line 423). Passed through so the host owns the wording.
   */
  sectionTitle?: string;
  /**
   * Render the ADDED NOTES slot at all. Default true.
   *
   * The EncounterScreen design draws no section-notes block on HPI (332-415), ROS (417-471) or
   * PE (473-535) — only A/P has one (line 647). Passing false OMITS the slot rather than hiding
   * it in CSS, because the populated editor renders through the DS `EncounterNoteEditor` with no
   * stable hook to target.
   *
   * THIS SUPPRESSES A WORKING FEATURE: the section narrative editor (PROD-1368), bound to the
   * authored `-narrative` linkId and persisted in the QuestionnaireResponse. Nothing is unwired —
   * flip this back to true and it returns.
   */
  showNotes?: boolean;
  /**
   * Open/close the section notes from the header's Comments icon.
   *
   * The design puts NO always-visible notes box on HPI/ROS/PE — the affordance is the Comments
   * glyph in the header row (lines 343 · 428 · 484), and the note surface appears on press.
   * Supplying this makes that icon live and reflects state via aria-pressed; omitting it keeps
   * the icon disabled, as the other no-backend actions are.
   */
  onToggleNotes?: () => void;
  /**
   * "Add Order" — the cluster icon that opens the shared left drawer. Present on EVERY encounter
   * tab except Wrap-up (Andrew's screenshot 2026-08-08), which is why it lives on this shared
   * chrome rather than on the A/P view.
   */
  onAddOrder?: () => void;
  /**
   * "Add to favourites" — the magnifier that opens the centre modal.
   *
   * ACCEPTED AND NOT RENDERED, on purpose. The host chain (`encounter-shell-page` →
   * `encounter-shell-view`) supplies this for every step except wrap-up, and it has never
   * produced a button here: the row branched on the `search` icon while `SECTION_ACTIONS`
   * never listed it, so the branch was unreachable. Kept in the signature so that chain
   * still typechecks — removing it means editing `encounter-shell-view`, which another
   * open PR is currently rewriting. The affordance itself is tracked; wire it there rather
   * than adding an icon here, or this becomes a button that appears without a decision.
   */
  onAddToFavourites?: () => void;

  /** Jump/scroll a group's card into view. Optional — omit to render chips non-interactive. */
  onJumpToGroup?: (linkId: string) => void;
  /**
   * Mark ONE category negative from the rail — the per-chip glyph in Flutter's category strip
   * (`common_encounter_component_base_tab.dart:658-697`, where each jump pill carries its own
   * all-negative icon beside the label). Optional: omit and the rail renders chips only, which
   * is what every surface did before PE gained the strip.
   *
   * It is a SIBLING button, never nested inside the chip — a button inside a button is invalid
   * HTML and would break the chip's own activation.
   */
  onAllNegGroup?: (linkId: string) => void;
  /** ADDED NOTES narrative binding. When supplied the editor is enabled; when omitted it's disabled. */
  notes?: EncounterSectionChromeNotes;
  /** Read-only (review/locked) hides the capture-only Clear + disables chips and the notes editor. */
  readOnly?: boolean;
  /** DS section identity (C4 / PROD-1588). REQUIRED since DS 1.52 shipped the full kind set
   *  (risk landed in cure wave 5) — the kind-less legacy layout is retired. */
  kind: EncounterSectionKind;
  /** D7 (Samer 2026-07-22): the ACTIVE template's real name → the DS chrome's read-only
   *  pill beside the section title. HONEST wiring — supplied only when the 5-axis template
   *  selection actually served a named template; omitted → no pill (never a hardcoded
   *  "Default"). */
  templateLabel?: string;
  /** The rail row's hairline — forwarded to the DS chrome (`strong` default · `soft`). */
  railBorder?: EncounterSectionChromeRailBorder;
  /** The section title's letter-spacing — forwarded to the DS chrome (`normal` default · `tight`). */
  titleTracking?: EncounterSectionChromeTitleTracking;
}

// The section-action row: only affordances that DO something. Andrew's call, 2026-08-20.
//
// It used to be [mic] [edit] [comment] [✦] with the first two permanently disabled, on the
// "visible per design intent, never wired to nothing" reading of anti-fiction. The decision
// reverses which way that principle points here: a control a clinician cannot use is itself the
// fiction, and two dead icons in every section header trained people to ignore the row that also
// holds the live ones. So the row now renders an action only when its handler is supplied — mic
// and edit have none and are gone, and `sparkle` disappears on wrap-up rather than greying out,
// which is where the host deliberately withholds it.
//
// PROD-1366 (dictate) and PROD-1367 (AI assist) still own the missing behaviour; when either
// gains a handler, add it back here and it will render because the handler exists, not because
// the array mentions it.
//
// ALSO NOT HERE: `search`, the Add-to-favourites magnifier. `encounter-shell-page` wires
// `onAddToFavourites` for every step except wrap-up and the code below branched on `search`, but
// this array never held it — so the branch was unreachable and the button has never rendered on
// an ordinary section. That stayed invisible because `IconName` used to widen to `string`, which
// let a comparison against a name outside this array compile; the real literal union in DS 1.59.0
// made it a type error, which is how it surfaced. Deliberately left OUT rather than restored —
// see the ticket — so this is behaviour-identical to what shipped, not a silent new button.
// A&P keeps its own magnifier via AP_SECTION_ACTIONS and is untouched.
export const SECTION_ACTIONS = [
  { name: 'message', glyph: MessageSquare, label: 'Comment on section' },
  { name: 'sparkle', glyph: Plus, label: 'Add Order' },
] satisfies { name: string; glyph: LucideIcon; label: string; ticket?: string }[];

export function EncounterSectionChromeHost({
  groups,
  onClearSection,
  onAllNeg,
  allNegLabel,
  sectionTitle,
  showNotes = true,
  onToggleNotes,
  onAddOrder,
  onJumpToGroup,
  onAllNegGroup,
  notes,
  readOnly = false,
  kind,
  templateLabel,
  railBorder,
  titleTracking,
}: EncounterSectionChromeProps) {
  // Design sheet v1.1, switch ON only (`?ef=frameV2`): Add Order draws `add` (K2).
  const designV2 = useDesignV2();
  // The DS-graduated organism IS the section chrome. PROD-1588 layering fix (Samer
  // 2026-07-22, "too white — frames not clear"): the chrome now renders its DS-default
  // `frame="card"` — measured live, the plain header/tag-rail/notes floated full-bleed on
  // the raw canvas (section rect 68..1492, bg transparent, no border/shadow) and the
  // attribute tags sat grey-on-grey. The elevated section-header card gives them a white
  // seat; the §6a block-grid stays a sibling below (the body-as-children composition is
  // still a follow-up). Anti-fiction action row stays: same four affordances, still
  // disabled. The wave-2 DS seams carry the behaviour:
  //   · the DS canonical full name renders ("History Of Present Illness", never the step
  //     short-label — the R3 audited drift this file used to ship).
  //   · Clear rides the DS `onClear` seam (interpolated "Clear HPI"/"Clear Risk"/…).
  //   · Per-group completion dots ride each rail link's `complete` prop (R3/B6 seam).
  return (
    <DsEncounterSectionChrome
      data-testid="encounter-section-chrome"
      titleTone="depth"
      // Design sheet v1.1 (switch ON only, DS 2.14.0 DCR-01): the solid wayfinding badge — the
      // section's SOAP category fill with a white glyph. OFF passes nothing (today's tint badge).
      {...(designV2 ? { badge: 'solid' as const, wayfind: CHROME_SECTION_WAYFIND[kind] } : {})}
      railBorder={railBorder}
      titleTracking={titleTracking}
      icon={kind}
      // D7 template pill — the DS read-only SelectionPill beside the title; renders only
      // when a REAL template name is supplied (see the prop doc — no fabricated "Default").
      templateLabel={templateLabel}
      // An action renders only when the host gave it something to do. `null` entries are
      // dropped rather than rendered disabled, so wrap-up — which withholds `onAddOrder` —
      // shows an empty row instead of a greyed sparkle.
      actions={SECTION_ACTIONS.map((a) =>
        a.name === 'sparkle' && onAddOrder ? (
          // `sparkle` is reserved for Scrips Intelligence (design sheet v1.1 §1 Icons): Add Order draws the plus in
          // both modes (icon pass); the dispatch still keys on the array entry, so the action cannot vanish.
          <SectionIconAction key={a.name} icon={a.glyph} label="Add Order" onClick={onAddOrder} />
        ) : a.name === 'message' && onToggleNotes ? (
          <SectionIconAction
            key={a.name}
            icon={a.glyph}
            // A closed note that HAS text says so: "Add section note" over a saved note read as
            // "your note is gone" (dev repro 2026-10-10).
            label={showNotes ? 'Hide section note' : notes?.value.trim() ? 'Show section note' : 'Add section note'}
            aria-pressed={showNotes}
            onClick={onToggleNotes}
          />
        ) : null,
      ).filter((node) => node !== null)}
      rail={
        groups.length > 0 ? (
          // The group jump-chips + per-category all-negative glyphs are the DS
          // `EncounterSectionRailGroups` (graduated from this file, 2026-09-23): ONE wrapper = ONE
          // rail child that takes the free space so the chrome's end-action pair lands hard right,
          // `data-slot="section-rail"`, `data-testid="rail-all-neg-<linkId>"`, the glyph a SIBLING
          // of the chip (never nested). The glyph renders only when the host wires the handler.
          <EncounterSectionRailGroups
            groups={groups}
            readOnly={readOnly}
            onJumpToGroup={onJumpToGroup}
            onAllNegGroup={onAllNegGroup}
          />
        ) : undefined
      }
      // Section-level All-Neg/All-Normal (I2): the DS rail end-slot link, before Clear —
      // capture-mode only (readOnly = signed/review ⇒ the link never renders → tap = no-op).
      onAllNeg={!readOnly ? onAllNeg : undefined}
      allNegLabel={allNegLabel}
      title={sectionTitle}
      onClear={groups.length > 0 && !readOnly ? onClearSection : undefined}
      notes={
        !showNotes ? undefined : notes ? (
          // DS EncounterNoteEditor (1.52) — read ⇄ edit saved-note card. Handlers 1:1 on the
          // existing QuestionnaireResponse seam: Save commits the trimmed draft; Delete clears
          // via onChange(''). In readOnly the pencil hides via the DS `readOnly` prop and the
          // delete affordance is omitted (the DS card renders onDelete even when readOnly —
          // a signed note must not be deletable).
          <EncounterNoteEditor
            key={notes.linkId}
            aria-label={notes.label ?? 'Added notes'}
            value={notes.value}
            placeholder="Add a narrative note…"
            readOnly={readOnly}
            onSave={(next) => notes.onChange(next.trim())}
            onDelete={readOnly ? undefined : () => notes.onChange('')}
          />
        ) : (
          // No authored narrative item — the DS `SectionAddedNotes`, visible but DISABLED
          // (anti-fiction). The chrome's `notes` slot draws its own overline, so no label.
          <SectionAddedNotes showLabel={false} />
        )
      }
    />
  );
}
